import React, { useState, useEffect } from "react";
import { Box, Container, Grid, Alert, useTheme, Button } from "@mui/material";
import {
  TrendingUp,
  Star,
  LocationOn,
  Collections,
  Info,
  Map,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import { DEBOUNCE_DELAYS, SEARCH } from "../../config/constants";
import { useAppDispatch, useAppSelector } from "../../store/store";
import {
  selectSelectedMallId,
  setSelectedMall,
} from "../../store/slices/mallsSlice";
import { useGetMallHubQuery } from "../../features/malls/mallsApiSlice";
import { useGetStoresByMallQuery } from "../../features/stores/storesApiSlice";
import OfferCard from "../../components/Offers/OfferCard";
import StoreCard from "../../components/Stores/StoreCard";
import EventCard from "../../components/Events/EventCard";
import {
  MallHubHero,
  MallHubQuickActions,
  MallHubSection,
  MallHubCTA,
  MallHubSkeleton,
  MallHubEmptyState,
  MallDetails,
  MallMap,
  MallGallery,
  InteractiveIndoorMap,
} from "../../components/MallHub";
import ScrollIndicator, {
  BottomFadeOverlay,
} from "../../components/common/ScrollIndicator";
import { gradients } from "../../utils/theme";

/**
 * MallHub Component
 *
 * Unified landing page for mall discovery - combines stores, offers and events
 * in a single, engaging portal experience. Designed for WhatsApp link sharing.
 *
 * Features:
 * - Unified search (stores + offers + events)
 * - Quick action cards
 * - Featured offers carousel
 * - Store categories grid
 * - Popular stores section
 * - Mobile-first responsive design
 */
const MallHub: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { mallId } = useParams<{ mallId: string }>();

  // Redux state
  const selectedMallId = useAppSelector(selectSelectedMallId);

  // Local state
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [showSearchResults, setShowSearchResults] = useState(false);
  const contentRef = React.useRef<HTMLDivElement>(null);

  // Fetch hub data (mall, stores, offers, events) in a single call
  const {
    data: hubData,
    isLoading: hubLoading,
    error: hubError,
    refetch: refetchHubData,
  } = useGetMallHubQuery(selectedMallId || "", {
    skip: !selectedMallId,
  });

  // Get mall info from hub response
  const mall = hubData?.mall;

  // Search stores in the selected mall only (debounced)
  const { data: storeSearchResults, isLoading: storeSearchLoading } =
    useGetStoresByMallQuery(
      {
        mallId: selectedMallId || "",
        searchQuery: debouncedSearchQuery,
      },
      {
        skip:
          !selectedMallId ||
          !debouncedSearchQuery ||
          debouncedSearchQuery.length < SEARCH.MIN_SEARCH_LENGTH,
      },
    );

  // Set mall from URL parameter
  useEffect(() => {
    if (mallId && selectedMallId !== mallId) {
      dispatch(setSelectedMall(mallId));
    }
  }, [mallId, selectedMallId, dispatch]);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, DEBOUNCE_DELAYS.STORE_SEARCH);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Handle scroll to slide content over hero section - Direct DOM manipulation
  React.useEffect(() => {
    const handleScroll = () => {
      if (contentRef.current) {
        const currentScrollY = window.scrollY;
        const maxOffset = 400;
        const offset = Math.min(currentScrollY, maxOffset);
        contentRef.current.style.transform = `translateY(-${offset}px)`;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Handle search
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    setShowSearchResults(query.length >= SEARCH.MIN_SEARCH_LENGTH);
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setDebouncedSearchQuery("");
    setShowSearchResults(false);
  };

  // Navigation handlers
  const handleViewAllOffers = () => {
    navigate(`/mall/${mallId}/offers`, { state: { mall: hubData?.mall } });
  };

  const handleViewAllStores = () => {
    navigate(`/mall/${mallId}/stores`, { state: { mall: hubData?.mall } });
  };

  const handleViewAllEvents = () => {
    navigate(`/mall/${mallId}/events`, { state: { mall: hubData?.mall } });
  };

  const featuredOffers = hubData?.offers?.slice(0, 10) || [];
  const upcomingEvents = hubData?.events?.slice(0, 10) || [];
  const popularStores = hubData?.stores?.slice(0, 10) || [];
  const totalOffers = hubData?.offers?.length || 0;
  const totalEvents = hubData?.events?.length || 0;
  const totalStores = hubData?.stores?.length || 0;
  const searchStores = storeSearchResults?.stores || [];
  const hasSearchResults = searchStores.length > 0;

  // Check if we're in debounce state (user is still typing)
  const isDebouncing =
    searchQuery !== debouncedSearchQuery &&
    searchQuery.length >= SEARCH.MIN_SEARCH_LENGTH;
  // Show loading when debouncing or API is loading
  const isSearching = isDebouncing || storeSearchLoading;

  // Loading states
  const isLoading = hubLoading;
  const hasError = hubError;

  // No mall selected state
  if (!selectedMallId) {
    return (
      <MallHubEmptyState onNavigateToExplore={() => navigate("/explore")} />
    );
  }

  if (isLoading) {
    return <MallHubSkeleton />;
  }

  if (hasError || !mall) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 3,
        }}
      >
        <Box sx={{ textAlign: "center", maxWidth: 600 }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            Unable to load mall information. Please try again later.
          </Alert>
          <Button
            variant="contained"
            size="large"
            onClick={() => refetchHubData()}
          >
            Retry
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `linear-gradient(135deg, ${theme.palette.primary.main}08 0%, ${theme.palette.secondary.main}08 100%)`,
      }}
    >
      {/* Hero Section with Search */}
      <MallHubHero
        mall={mall!}
        totalOffers={totalOffers}
        totalStores={totalStores}
        upcomingEventsCount={totalEvents}
        searchQuery={searchQuery}
        onSearchChange={handleSearchChange}
        onClearSearch={handleClearSearch}
        showSearchResults={showSearchResults}
        isSearching={isSearching}
        hasSearchResults={hasSearchResults}
        debouncedSearchQuery={debouncedSearchQuery}
        searchStores={searchStores}
        onCloseSearch={() => setShowSearchResults(false)}
        isSearchActive={showSearchResults || isSearching}
      />

      {/* Main Content - Slides up over hero on scroll */}
      <Box
        ref={contentRef}
        sx={{
          bgcolor: "white",
          py: { xs: 3, md: 4 },
          position: "relative",
          willChange: "transform",
          borderTopLeftRadius: { xs: 16, md: 24 },
          borderTopRightRadius: { xs: 16, md: 24 },
          boxShadow: "0 -4px 20px rgba(0,0,0,0.1)",
          zIndex: 1100,
        }}
      >
        <Container maxWidth="lg">
          {/* Quick Action Cards */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <MallHubQuickActions
              totalOffers={totalOffers}
              totalStores={totalStores}
              totalEvents={totalEvents}
              onViewOffers={handleViewAllOffers}
              onViewStores={handleViewAllStores}
              onViewEvents={handleViewAllEvents}
            />
          </motion.div>

          {/* Featured Offers Section */}
          {totalOffers > 0 && (
            <MallHubSection
              icon={<TrendingUp sx={{ color: "white", fontSize: 24 }} />}
              iconBackground={gradients.gold}
              title="Offers"
              subtitle={`Hot deals you don't want to miss • ${totalOffers} available`}
              onViewAll={handleViewAllOffers}
              delay={0.4}
            >
              {/* Mobile: Horizontal Scroll */}
              <Box
                sx={{
                  display: { xs: "flex", md: "none" },
                  overflowX: "auto",
                  overflowY: "hidden",
                  gap: 2,
                  pb: 2,
                  scrollSnapType: "x mandatory",
                  WebkitOverflowScrolling: "touch",
                  "&::-webkit-scrollbar": { display: "none" },
                  scrollbarWidth: "none",
                }}
              >
                {featuredOffers.map((offer) => (
                  <Box
                    key={offer.id}
                    sx={{
                      minWidth: { xs: "85%", sm: "45%" },
                      scrollSnapAlign: "start",
                      scrollSnapStop: "always",
                    }}
                  >
                    <OfferCard offer={offer} />
                  </Box>
                ))}
              </Box>

              {/* Desktop: Grid Layout */}
              <Grid
                container
                spacing={3}
                sx={{ display: { xs: "none", md: "flex" } }}
              >
                {featuredOffers.map((offer) => (
                  <Grid item xs={12} sm={6} md={4} key={offer.id}>
                    <OfferCard offer={offer} />
                  </Grid>
                ))}
              </Grid>
            </MallHubSection>
          )}

          {/* Upcoming Events Section */}
          {totalEvents > 0 && (
            <MallHubSection
              icon={<span style={{ fontSize: 24 }}>🎉</span>}
              iconBackground={gradients.emerald}
              title="Upcoming Events"
              subtitle={`Don't miss out on exciting happenings • ${totalEvents} upcoming`}
              onViewAll={handleViewAllEvents}
              delay={0.45}
            >
              {/* Mobile: Horizontal Scroll */}
              <Box
                sx={{
                  display: { xs: "flex", md: "none" },
                  overflowX: "auto",
                  overflowY: "hidden",
                  gap: 2,
                  pb: 2,
                  scrollSnapType: "x mandatory",
                  WebkitOverflowScrolling: "touch",
                  "&::-webkit-scrollbar": { display: "none" },
                  scrollbarWidth: "none",
                }}
              >
                {upcomingEvents.map((event) => (
                  <Box
                    key={event.id}
                    sx={{
                      minWidth: { xs: "85%", sm: "45%" },
                      scrollSnapAlign: "start",
                      scrollSnapStop: "always",
                    }}
                  >
                    <EventCard event={event} />
                  </Box>
                ))}
              </Box>

              {/* Desktop: Grid Layout */}
              <Grid
                container
                spacing={3}
                sx={{ display: { xs: "none", md: "flex" } }}
              >
                {upcomingEvents.map((event) => (
                  <Grid item xs={12} sm={6} md={4} key={event.id}>
                    <EventCard event={event} />
                  </Grid>
                ))}
              </Grid>
            </MallHubSection>
          )}

          {/* Popular Stores Section */}
          {totalStores > 0 && (
            <MallHubSection
              icon={<Star sx={{ color: "white", fontSize: 24 }} />}
              iconBackground={gradients.greenAlt}
              title="Stores"
              subtitle={`Most visited by shoppers • ${totalStores} stores`}
              onViewAll={handleViewAllStores}
              delay={0.6}
            >
              {/* Mobile: Horizontal Scroll */}
              <Box
                sx={{
                  display: { xs: "flex", md: "none" },
                  overflowX: "auto",
                  overflowY: "hidden",
                  gap: 2,
                  pb: 2,
                  scrollSnapType: "x mandatory",
                  WebkitOverflowScrolling: "touch",
                  "&::-webkit-scrollbar": { display: "none" },
                  scrollbarWidth: "none",
                }}
              >
                {popularStores.map((store) => (
                  <Box
                    key={store.id}
                    sx={{
                      minWidth: { xs: "70%", sm: "40%" },
                      scrollSnapAlign: "start",
                      scrollSnapStop: "always",
                    }}
                  >
                    <StoreCard store={store} />
                  </Box>
                ))}
              </Box>

              {/* Desktop: Grid Layout */}
              <Grid
                container
                spacing={3}
                sx={{ display: { xs: "none", md: "flex" } }}
              >
                {popularStores.map((store) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={store.id}>
                    <StoreCard store={store} />
                  </Grid>
                ))}
              </Grid>
            </MallHubSection>
          )}

          {/* Location & Mall Information Section - Combined */}
          <Box sx={{ mt: { xs: 3, md: 4 } }}>
            <Grid container spacing={{ xs: 3, md: 3, lg: 4 }}>
              {/* Details Column - Show First on Mobile */}
              <Grid item xs={12} md={6} order={{ xs: 2, md: 1 }}>
                <MallHubSection
                  icon={<Info sx={{ color: "white", fontSize: 24 }} />}
                  iconBackground={gradients.cyan}
                  title="Mall Information"
                  subtitle="Contact & operating hours"
                  delay={0.55}
                >
                  <MallDetails
                    location={mall.location}
                    city={mall.city}
                    state={mall.state}
                    pincode={mall.pincode}
                    openingHours={mall.openingHours}
                    operatingDays={mall.operatingDays}
                    contactNumber={mall.contactNumber}
                    email={mall.email}
                    website={mall.website}
                  />
                </MallHubSection>
              </Grid>

              {/* Map Column - Show First on Desktop */}
              <Grid item xs={12} md={6} order={{ xs: 1, md: 2 }}>
                <MallHubSection
                  icon={<LocationOn sx={{ color: "white", fontSize: 24 }} />}
                  iconBackground={gradients.navyLux}
                  title="Location & Directions"
                  subtitle="Find us easily with interactive map"
                  delay={0.6}
                >
                  <MallMap
                    latitude={mall.coordinates?.lat}
                    longitude={mall.coordinates?.lng}
                    mallName={mall.name}
                    address={mall.location}
                  />
                </MallHubSection>
              </Grid>
            </Grid>
          </Box>

          {/* Interactive Indoor Map Section - Demo/Dev Only */}
          {process.env.REACT_APP_SHOW_INDOOR_MAP_FEATURE === "true" && (
            <MallHubSection
              icon={<Map sx={{ color: "white", fontSize: 24 }} />}
              iconBackground={gradients.emerald}
              title="Interactive Indoor Map"
              subtitle="Navigate the mall with AR-powered wayfinding"
              delay={0.65}
            >
              <InteractiveIndoorMap mallName={mall.name} />
            </MallHubSection>
          )}

          {/* Gallery Section */}
          {mall.galleryImages && mall.galleryImages.length > 0 && (
            <MallHubSection
              icon={<Collections sx={{ color: "white", fontSize: 24 }} />}
              iconBackground={gradients.pink}
              title="Gallery"
              subtitle={`Explore ${mall.name} • ${mall.galleryImages.length} photos`}
              delay={0.7}
            >
              <MallGallery images={mall.galleryImages} mallName={mall.name} />
            </MallHubSection>
          )}

          {/* Call to Action Banner */}
          <MallHubCTA
            totalOffers={totalOffers}
            totalStores={totalStores}
            totalEvents={totalEvents}
            onViewOffers={handleViewAllOffers}
            onViewStores={handleViewAllStores}
            onViewEvents={handleViewAllEvents}
          />
        </Container>
      </Box>

      {/* Scroll Indicator for Mobile Users */}
      <ScrollIndicator color="primary.main" bottomOffset={100} />
      <BottomFadeOverlay color="#f5f7fa" height={100} />
    </Box>
  );
};

export default MallHub;
